import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, ShieldCheck, Send } from "lucide-react";
import { Reveal } from "./motion";
import { BUSINESS, SERVICE_OPTIONS } from "@/data/siteData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-ink text-sm placeholder:text-slate2/60 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition";

export default function BookContact({ standalone = false }) {
  const Heading = standalone ? "h1" : "h2";
  const [form, setForm] = useState({ name: "", phone: "", email: "", vehicle: "", service: "", city: "", preferred_date: "", preferred_time: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.vehicle || !form.service || !form.city) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Request received! We'll confirm your appointment shortly.");
      setForm({ name: "", phone: "", email: "", vehicle: "", service: "", city: "", preferred_date: "", preferred_time: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="book-section" className="bg-white py-20 lg:py-28 scroll-mt-20">
      <div id="contact" className="max-w-7xl mx-auto px-5 lg:px-8 scroll-mt-24 grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        {/* Info */}
        <div className="lg:col-span-2">
          <Reveal>
            <p className="text-brand font-semibold text-sm tracking-wide uppercase">Book Appointment</p>
            <Heading className="font-display font-bold text-ink tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.1]">
              Schedule your verification
            </Heading>
            <p className="text-slate2 text-base mt-4 leading-relaxed">
              Tell us where you are and what you drive. A California licensed verifier will confirm your appointment — usually same or next day.
            </p>

            <div className="mt-8 space-y-4">
              <a href={BUSINESS.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 text-ink hover:text-brand transition-colors">
                <span className="h-11 w-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center"><Phone size={18} /></span>
                <span className="font-semibold">{BUSINESS.phone}</span>
              </a>
              <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="flex items-center gap-4 text-ink hover:text-brand transition-colors">
                <span className="h-11 w-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center"><Mail size={18} /></span>
                <span>{BUSINESS.email}</span>
              </a>
              <div className="flex items-center gap-4 text-slate2">
                <span className="h-11 w-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center"><Clock size={18} /></span>
                <span>{BUSINESS.hours}</span>
              </div>
              <div className="flex items-center gap-4 text-slate2">
                <span className="h-11 w-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center"><ShieldCheck size={18} /></span>
                <span>{BUSINESS.license}</span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="booking-form" className="card-soft hover:!translate-y-0 p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="input-name" name="name" required autoComplete="name" aria-label="Full name" className={field} placeholder="Full name *" value={form.name} onChange={set("name")} />
              <input data-testid="input-phone" name="phone" type="tel" required autoComplete="tel" inputMode="tel" aria-label="Phone" className={field} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
              <input data-testid="input-email" name="email" type="email" required autoComplete="email" aria-label="Email" className={field} placeholder="Email *" value={form.email} onChange={set("email")} />
              <input data-testid="input-city" name="city" required autoComplete="address-level2" aria-label="City" className={field} placeholder="City *" value={form.city} onChange={set("city")} />
              <input data-testid="input-vehicle" name="vehicle" required aria-label="Vehicle year, make and model" className={`${field} sm:col-span-2`} placeholder="Vehicle (year / make / model) *" value={form.vehicle} onChange={set("vehicle")} />

              <div className="sm:col-span-2">
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="select-service" aria-label="Service" className="w-full bg-white border-gray-200 text-ink h-[46px] rounded-xl focus:ring-2 focus:ring-brand/15">
                    <SelectValue placeholder="Select a service *" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <input data-testid="input-date" name="preferred_date" type="date" min={today} aria-label="Preferred date" className={field} value={form.preferred_date} onChange={set("preferred_date")} />
              <input data-testid="input-time" name="preferred_time" type="time" aria-label="Preferred time" className={field} value={form.preferred_time} onChange={set("preferred_time")} />
              <textarea data-testid="input-message" name="message" rows={3} aria-label="Additional information" className={`${field} sm:col-span-2 resize-none`} placeholder="Anything we should know? (optional)" value={form.message} onChange={set("message")} />

              <button data-testid="booking-submit" type="submit" disabled={loading} className="btn-accent sm:col-span-2 rounded-full px-8 py-4 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? "Sending..." : <>Request Appointment <Send size={16} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
